import { Col, DatePicker, Row } from "antd";
import { ImageUrl } from "../../config/helpers";
import { useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import { Get } from "../../config/api/get";
import { ADMIN } from "../../config/constants/api";
import dayjs from "dayjs";

const Dashboard = () => {
  const token = useSelector((state) => state.user.userToken);
  const [info, setInfo] = useState(null);
  const [paymentStats, setPaymentStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());

  const getDashboardInfo = () => {
    Get(ADMIN.dashboardInfo, token)
      .then((response) => {
        if (response?.status || response?.success) {
          setInfo(response?.data);
        }
      })
      .catch((err) => {
        console.log("Error fetching dashboard info ", err);
      });
  };

  const getPaymentStats = (year) => {
    Get(ADMIN.paymentStats, token, { year })
      .then((response) => {
        // Check if response is an error object (from Get function)
        if (response?.response || (response?.message && !response?.status)) {
          console.error("Payment stats API error:", response);
          return;
        }
        if ((response?.status || response?.success) && response?.data) {
          setPaymentStats(response.data);
        } else {
          console.warn("Payment stats response format unexpected:", response);
        }
      })
      .catch((err) => {
        console.error("Error fetching payment stats:", err);
      });
  };

  const getUserStats = (year) => {
    Get(ADMIN.userStats, token, { year })
      .then((response) => {
        // Check if response is an error object (from Get function)
        if (response?.response || (response?.message && !response?.status)) {
          console.error("User stats API error:", response);
          return;
        }
        if ((response?.status || response?.success) && response?.data) {
          setUserStats(response.data);
        } else {
          console.warn("User stats response format unexpected:", response);
        }
      })
      .catch((err) => {
        console.error("Error fetching user stats:", err);
      });
  };

  useEffect(() => {
    console.log("Dashboard useEffect triggered, selectedYear:", selectedYear);
    getDashboardInfo();
    getPaymentStats(selectedYear);
    getUserStats(selectedYear);
    // eslint-disable-next-line
  }, [selectedYear]);
  const items = [
    {
      image: "person.png",
      heading: "Total Number of Users",
      count: info?.totalUsers ,
      percent: 100,
    },
    {
      image: "steering.png",
      heading: "Total Drivers",
      count: info?.totalChauffers ,
      percent: 100,
    },
    {
      image: "revenue.png",
      heading: "Total Revenue",
      count:  `$${info?.totalRevenue.toFixed(2)}`,
      percent: 100,
    },
  ];
  const CustomBox = ({ image, heading, count, percent }) => (
    <Col md={8}>
      <div
        className="bg-white rounded-lg"
        style={{ boxShadow: "1px 1px 1px 1px rgba(0, 0, 0, 0.05)" }}
      >
        <div className="py-6 px-4 flex justify-start items-center border-b border-gray-200">
          <img src={ImageUrl(image)} alt="icon" />
          <div className="flex flex-col items-start justify-center ml-3">
            <p className="text-lg text-[#333333] font-euclid font-bold">
              {heading}
            </p>
            <p className="text-3xl text-[#1E1E1E] font-euclid font-bold">
              {count}
            </p>
          </div>
        </div>
      </div>
    </Col>
  );
  const handleYearChange = (date) => {
    if (date) {
      const year = date.year();
      setSelectedYear(year);
    }
  };

  // Helper function to calculate max value for chart scaling
  const getMaxValue = (data, key) => {
    if (!data || !Array.isArray(data)) return 100;
    const max = Math.max(...data.map((item) => item[key] || 0));
    return max > 0 ? max * 1.1 : 100; // Add 10% padding
  };

  // Helper function to get month name from month number
  const getMonthName = (monthNumber) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return months[monthNumber - 1] || "";
  };

  // Render line chart component (styled similar to original PNG charts)
  const LineChart = ({
    data,
    valueKey,
    labelKey = "label",
    height = 320,
    isRevenue = false,
  }) => {
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const chartContainerRef = useRef(null);

    if (!data || !Array.isArray(data) || data.length === 0) {
      return (
        <div
          className="rounded-lg flex items-center justify-center"
          style={{ height: `${height}px` }}
        >
          <p className="text-sm font-euclid text-white/80">No data available</p>
        </div>
      );
    }

    const maxValue = getMaxValue(data, valueKey);
    const pointRadius = 4;

    // Use proper aspect ratio (3:1 for wide chart) to prevent stretching
    const viewBoxWidth = 1200;
    const viewBoxHeight = 400;
    const paddingLeft = 80;
    const paddingRight = 80;
    const paddingTop = 50;
    const paddingBottom = 50;
    const chartWidth = viewBoxWidth - paddingLeft - paddingRight;
    const chartHeight = viewBoxHeight - paddingTop - paddingBottom;

    // Calculate points for the line
    const points = data.map((item, index) => {
      const x =
        paddingLeft + (index / (data.length - 1 || 1)) * chartWidth;
      const y =
        paddingTop +
        (maxValue > 0
          ? chartHeight - (item[valueKey] / maxValue) * chartHeight
          : chartHeight);
      return {
        x,
        y,
        value: item[valueKey],
        label: item[labelKey],
        month: item.month,
        originalData: item,
      };
    });

    // Handle mouse events for tooltip
    const handleMouseEnter = (point, index, event) => {
      if (chartContainerRef.current) {
        const container = chartContainerRef.current;
        const svg = event.currentTarget.closest('svg');
        if (!svg) return;
        
        const containerRect = container.getBoundingClientRect();
        
        // Calculate position in percentage of viewBox
        const xPercent = (point.x / viewBoxWidth) * 100;
        const yPercent = (point.y / viewBoxHeight) * 100;
        
        // Convert to pixel position relative to container
        const x = (xPercent / 100) * containerRect.width;
        const y = (yPercent / 100) * containerRect.height;
        
        setTooltipPosition({ x, y });
        setHoveredPoint({ ...point, index });
      }
    };

    const handleMouseLeave = () => {
      setHoveredPoint(null);
    };

    // Create path for the line
    const linePath = points
      .map((point, index) => {
        return `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`;
      })
      .join(" ");

    // Create path for the area under the line
    const areaPath = `${linePath} L ${
      points[points.length - 1].x
    } ${viewBoxHeight - paddingBottom} L ${points[0].x} ${
      viewBoxHeight - paddingBottom
    } Z`;

    // Colors based on chart type - matching original image colors
    const lineColor = "#FFFFFF";
    const gradientId = isRevenue ? "revenueGradient" : "userGradient";
    const gradientColors = isRevenue
      ? { start: "#FFFFFF", end: "#FFFFFF", opacity: 0.35 }
      : { start: "#FFFFFF", end: "#FFFFFF", opacity: 0.35 };

    const backgroundStyle = isRevenue
      ? { backgroundColor: "#A97606" }
      : {
          background:
            "linear-gradient(180deg, #2456D3 0%, #123C9F 50%, #081F61 100%)",
        };

    const gridLines = 4;

    return (
      <div
        className="rounded-lg px-8 py-6"
        style={backgroundStyle}
      >
        <div className="flex items-center" style={{ height: `${height - 90}px` }}>
          {/* Y-axis label */}
          <div className="flex flex-col items-center justify-center mr-4 text-[11px] tracking-[3px] text-white/80 uppercase">
            <span className="-rotate-90">
              {isRevenue ? "Revenue" : "Users"}
            </span>
          </div>

          <div 
            ref={chartContainerRef}
            style={{ position: "relative", height: "100%", width: "100%" }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
              style={{ position: "absolute", top: 0, left: 0 }}
              // allow chart to stretch full width/height of container
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient
                  id={gradientId}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop
                    offset="0%"
                    stopColor={gradientColors.start}
                    stopOpacity={gradientColors.opacity}
                  />
                  <stop
                    offset="100%"
                    stopColor={gradientColors.end}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>

              {/* Horizontal grid lines */}
              {Array.from({ length: gridLines + 1 }).map((_, index) => {
                const y =
                  paddingTop + (index / gridLines) * chartHeight;
                return (
                  <line
                    key={index}
                    x1={paddingLeft}
                    x2={viewBoxWidth - paddingRight}
                    y1={y}
                    y2={y}
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth="1"
                    strokeDasharray="4 6"
                  />
                );
              })}

              {/* Area under the line */}
              <path d={areaPath} fill={`url(#${gradientId})`} />

              {/* Line */}
              <path
                d={linePath}
                fill="none"
                stroke={lineColor}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Data points */}
              {points.map((point, index) => (
                <circle
                  key={index}
                  cx={point.x}
                  cy={point.y}
                  r={pointRadius}
                  fill={lineColor}
                  stroke={lineColor}
                  strokeWidth="2"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => handleMouseEnter(point, index, e)}
                  onMouseLeave={handleMouseLeave}
                />
              ))}
              
              {/* Invisible larger hit area for easier hovering */}
              {points.map((point, index) => (
                <circle
                  key={`hit-${index}`}
                  cx={point.x}
                  cy={point.y}
                  r={pointRadius + 6}
                  fill="transparent"
                  stroke="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => handleMouseEnter(point, index, e)}
                  onMouseLeave={handleMouseLeave}
                />
              ))}
            </svg>
            
            {/* Tooltip */}
            {hoveredPoint && (
              <div
                className="absolute pointer-events-none z-10"
                style={{
                  left: `${(tooltipPosition.x / (chartContainerRef.current?.clientWidth || 1)) * 100}%`,
                  top: `${(tooltipPosition.y / (chartContainerRef.current?.clientHeight || 1)) * 100}%`,
                  transform: "translate(-50%, -120%)",
                }}
              >
                <div
                  className="bg-black/90 text-white rounded-lg px-3 py-2 shadow-lg"
                  style={{ minWidth: "120px" }}
                >
                  <div className="text-xs font-euclid font-bold mb-1">
                    {hoveredPoint.month 
                      ? getMonthName(hoveredPoint.month) 
                      : hoveredPoint.label}
                  </div>
                  <div className="text-sm font-euclid">
                    {isRevenue ? "$" : ""}
                    {typeof hoveredPoint.value === "number"
                      ? hoveredPoint.value.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : hoveredPoint.value}
                    {!isRevenue && " users"}
                  </div>
                </div>
                {/* Tooltip arrow */}
                <div
                  className="absolute left-1/2 top-full"
                  style={{
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "6px solid transparent",
                    borderRight: "6px solid transparent",
                    borderTop: "6px solid rgba(0, 0, 0, 0.9)",
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* X-axis labels - aligned with chart points */}
        <div className="flex items-center mt-4" style={{ minHeight: "24px" }}>
          {/* Spacer for Y-axis label */}
          <div className="mr-4" style={{ width: "48px" }}></div>
          {/* Chart area with labels */}
          <div className="relative flex-1">
            {data.map((item, index) => {
              // Mirror the same x calculation as for the points
              const fraction = index / (data.length - 1 || 1);
              const x =
                paddingLeft + fraction * chartWidth; // 0–viewBoxWidth space
              const xPercent = (x / viewBoxWidth) * 100;
              return (
                <span
                  key={index}
                  className="text-xs font-euclid text-white/80 text-center absolute"
                  style={{
                    left: `${xPercent}%`,
                    transform: "translateX(-50%)",
                  }}
                >
                  {item.month ? getMonthName(item.month) : item[labelKey]}
                </span>
              );
            })}
          </div>
        </div>
        <div className="mt-4 flex justify-center">
          <span className="text-[11px] tracking-[3px] uppercase text-white/70">
            Months
          </span>
        </div>
      </div>
    );
  };
  return (
    <Row justify="center" className="py-8">
      <Col md={23}>
        <h3 className="font-tavi text-3xl font-normal text-black ml-4 mb-4">
          Dashboard
        </h3>
       {info && <Row justify="center" gutter={[32, 32]}>
          {items.map((item) => (
            <CustomBox
              key={item.heading}
              image={item.image}
              heading={item.heading}
              count={item.count}
              percent={item.percent}
            />
          ))}
        </Row>}
        <div className="flex justify-between items-center mb-3 mt-8">
          <div className="flex flex-col items-start ml-4">
            <h3 className="font-tavi text-3xl font-normal text-black">
              Payment Stats
            </h3>
            <p className="text-sm font-euclid text-[#7B809A]">
              {paymentStats?.summary?.percentChange !== undefined ? (
                <>
                  (<span className="font-bold">
                    {paymentStats.summary.percentChange >= 0 ? "+" : ""}
                    {paymentStats.summary.percentChange.toFixed(1)}%
                  </span>) increase in {selectedYear} revenue.
                </>
              ) : (
                "(+15%) increase in today revenue."
              )}
            </p>
          </div>
          <DatePicker
            onChange={handleYearChange}
            picker="year"
            className="py-2"
            value={dayjs(`${selectedYear}-01-01`)}
          />
        </div>
        <LineChart
          data={paymentStats?.data}
          valueKey="totalRevenue"
          labelKey="label"
          height={320}
          isRevenue={true}
        />

        <div className="flex justify-between items-center mb-3 mt-8">
          <div className="flex flex-col items-start ml-4">
            <h3 className="font-tavi text-3xl font-normal text-black">
              User Statistics
            </h3>
            <p className="text-sm font-euclid text-[#7B809A]">
              {userStats?.summary?.percentChange !== undefined ? (
                <>
                  (<span className="font-bold">
                    {userStats.summary.percentChange >= 0 ? "+" : ""}
                    {userStats.summary.percentChange.toFixed(1)}%
                  </span>) increase in {selectedYear} users.
                </>
              ) : (
                "(+15%) increase in today users."
              )}
            </p>
          </div>
          <DatePicker
            onChange={handleYearChange}
            picker="year"
            className="py-2"
            value={dayjs(`${selectedYear}-01-01`)}
          />
        </div>
        <LineChart
          data={userStats?.data}
          valueKey="newUsers"
          labelKey="label"
          height={320}
          isRevenue={false}
        />
      </Col>
    </Row>
  );
};

export default Dashboard;
